import { FeatureFlags } from '../types/appConfig';
import { fetchData, hasToken } from '../utils/apiClient';
import { UNAUTHORIZED_PATH, FORBIDDEN_PATH, STATIC_ROUTES, EQAT_OIDC_CALLBACK_PATH, LOGOUT_PATH } from '../constants';
import env from '../env';
import debug from 'debug';

const log = debug('qap-web:appconfig');

const CACHE_KEY = 'qap_feature_flags_cache';

// Paths where we should not attempt API calls to avoid redirect loops
// Include OIDC callback paths — a stale cookie + 403 here aborts login and can loop
const ERROR_PATHS = [
  UNAUTHORIZED_PATH,
  FORBIDDEN_PATH,
  STATIC_ROUTES,
  EQAT_OIDC_CALLBACK_PATH,
  LOGOUT_PATH,
  env.OIDC_REDIRECT_URI,
  env.OIDC_REFRESH_URI,
];

class AppConfigService {
  private flags: FeatureFlags = {};
  private pollInterval: NodeJS.Timeout | null = null;
  private listeners: Set<(flags: FeatureFlags) => void> = new Set();

  /**
   * Fetch feature flags from the backend
   * Checks cache first to avoid unnecessary API calls
   */
  async fetchFlags(forceRefresh: boolean = false): Promise<FeatureFlags> {
    if (!env.APP_CONFIG_ENABLED) {
      log('AppConfig is disabled, returning empty flags');
      return {};
    }

    // Skip API calls on error pages to prevent redirect loops
    // When on unauthorized/forbidden pages, a 401/403 response would redirect
    // back to the same page, causing an infinite loop
    if (ERROR_PATHS.includes(window.location.pathname)) {
      log('On error page, skipping feature flags fetch to prevent redirect loop');
      const cached = this.getCachedFlags();
      if (cached) {
        this.flags = cached;
        return cached;
      }
      return {};
    }

    // Check cache first - if valid cache exists and not forcing refresh, return it
    if (!forceRefresh) {
      const cached = this.getCachedFlags();
      if (cached) {
        log('Using cached feature flags');
        this.flags = cached;
        return cached;
      }
    }

    // Check if user is authenticated before attempting to fetch
    // This prevents redirect loops when auth is not yet complete
    if (!hasToken()) {
      log('No auth token available, returning cached or empty flags');
      const cached = this.getCachedFlags();
      if (cached) {
        this.flags = cached;
        return cached;
      }
      return {};
    }

    try {
      const endpoint = env.APP_CONFIG_ENDPOINT || '/config/feature-flags';
      
      log('Fetching feature flags from:', endpoint);

      const data = await fetchData(endpoint, 'GET_FEATURE_FLAGS');
      const flags = this.transformResponse(data);

      
      this.cacheFlags(flags);
      this.flags = flags;
      this.notifyListeners(flags);

      log('Feature flags fetched and cached:', flags);
      return flags;
    } catch (error) {
      log('Error fetching flags:', error);
      
      // On error, try to use cached flags as fallback
      const cached = this.getCachedFlags();
      if (cached) {
        log('Using cached flags as fallback after API error');
        this.flags = cached;
        return cached;
      }

      return {};
    }
  }

  /**
   * Transform API response to FeatureFlags format
   */
  private transformResponse(data: any): FeatureFlags {
    const flags: FeatureFlags = {};

    // Handle { featureFlags: { ... } } format
    if (data.featureFlags) {
      Object.entries(data.featureFlags).forEach(([key, value]: [string, any]) => {
        flags[key] = {
          enabled: typeof value === 'boolean' ? value : value?.enabled ?? false,
        };
      });
      return flags;
    }

    // Handle { flags: { ... } } format (AWS AppConfig Feature Flags)
    if (data.flags) {
      Object.entries(data.flags).forEach(([key, value]: [string, any]) => {
        flags[key] = {
          enabled: value?.enabled ?? value === true,
        };
      });
      return flags;
    }

    // Handle flat format { flagName: true/false }
    Object.entries(data).forEach(([key, value]: [string, any]) => {
      flags[key] = {
        enabled: typeof value === 'boolean' ? value : value?.enabled ?? false,
      };
    });

    return flags;
  }

  private cacheFlags(flags: FeatureFlags): void {
    try {
      // Store flags without expiration - cache persists until manually cleared
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ flags, timestamp: Date.now() }));
      log('Feature flags cached (no expiration - will persist until manually cleared)');
    } catch (error) {
      log('Failed to cache flags:', error);
    }
  }

  private getCachedFlags(): FeatureFlags | null {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const { flags } = JSON.parse(cached);
      // Cache never expires - only cleared manually
      return flags;
    } catch {
      return null;
    }
  }

  startPolling(intervalMs?: number): void {
    if (this.pollInterval) return;

    const interval = intervalMs || env.APP_CONFIG_POLL_INTERVAL || 300000;
    log(`Starting polling with interval: ${interval}ms`);
    
    this.pollInterval = setInterval(() => {
      // Only fetch if cache is missing (cache never expires, only cleared manually)
      const cached = this.getCachedFlags();
      if (!cached) {
        // Cache missing, fetch fresh data
        this.fetchFlags(true).catch(log);
      } else {
        log('Cache exists, skipping poll fetch (cache must be manually cleared to refresh)');
      }
    }, interval);
  }

  stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  subscribe(listener: (flags: FeatureFlags) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(flags: FeatureFlags): void {
    this.listeners.forEach((listener) => listener(flags));
  }

  getFlags(): FeatureFlags {
    return this.flags;
  }

  isFeatureEnabled(flagName: string): boolean {
    return this.flags[flagName]?.enabled ?? false;
  }

  clearCache(): void {
    this.flags = {};
    sessionStorage.removeItem(CACHE_KEY);
  }
}

export const appConfigService = new AppConfigService();
export default appConfigService;
